import React, { useState, useEffect, useCallback } from "react";
import { Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

import { handleUnauthorized, setSessionId, clearSessionId, getSessionId } from "./utils/api";
import * as authApi from "./api/auth";
import { stopImpersonation } from "./api/admin";
import Header from "./components/Header";
import Footer from "./components/Footer";
import AuthPage from "./components/AuthPage";
import KnowledgeBase from "./components/KnowledgeBase";
import Dashboard from "./components/Dashboard";
import DebugDashboard from "./components/DebugDashboard";
import ResetPassword from "./components/ResetPassword";
import AdminImpersonation from "./components/AdminImpersonation";
import SignUp from "./components/SignUp";
import EmailVerification from "./components/EmailVerification";
import Account from "./components/Accounts";
import ContactUs from "./components/ContactUs";
import TermsAndConditions from "./components/TermsAndConditions";
import PrivacyPolicies from "./components/PrivacyPolicies";
import About from "./components/About";
import BlogGrid from "./components/BlogGrid";
import BlogPost from "./components/BlogPost";
import Pricing from "./components/Pricing";
import Profile from "./components/Profile";
import TaxSavingsCalculator from "./components/TaxSavingsCalculator";
import DemoComponent from "./components/DemoComponent";
import ComparisonTable from "./components/ComparisonTable";
import ChatBot from "./components/ChatBot/ChatBot";
import ChatPage from "./components/chat/ChatPage";
import OverviewPage from "./components/OverviewPage";
import { HomePage } from "./pages/Home";
import "./App.css";
import Modal from "./components/Modal";
import TaxSavingsQuiz from './components/TaxSavingsQuiz';
import FAQ from './components/FAQ';
import ImpersonationBanner from "./components/ImpersonationBanner";
import TaxScenarioCalculator from "./components/TaxScenarioCalculator";

const backendUrl = "https://profitree-backend-9482feeffc4a.herokuapp.com";

// Component to redirect logged-in users away from auth pages
const AuthRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate("/tax-insights");
  }, [navigate]);
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
      <div>You are already logged in. Redirecting...</div>
    </div>
  );
};

function App() {
  const location = useLocation(); // Now it works because App is inside Router
  const navigate = useNavigate();
  const [isModalOpen, setModalOpen] = useState({
    open: false,
    type: "account",
  });
  const [isOnboardingProcessing, setIsOnboardingProcessing] = useState(false);

  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("config", "G-VHQR7N5W71", {
        page_path: location.pathname,
      });
    }
  }, [location]);

  const [username, setUsername] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionCheckInProgress, setSessionCheckInProgress] = useState(true);
  const [sessionDetails, setSessionDetails] = useState({
    isAdmin: false,
    impersonating: false,
    impersonatedUsername: null,
    adminUsername: null,
  });
  const [impersonationTransitioning, setImpersonationTransitioning] = useState(false);

  const parseSessionDetails = useCallback((userData = {}) => {
    const resolvedUsername = userData.username || userData.email || null;
    const rawRoles =
      userData.roles ||
      userData.authorities ||
      userData.role ||
      userData.authority ||
      [];

    const rolesArray = Array.isArray(rawRoles)
      ? rawRoles
      : typeof rawRoles === "string"
      ? [rawRoles]
      : [];

    const isAdmin =
      typeof userData.isAdmin === "boolean"
        ? userData.isAdmin
        : rolesArray.some((role) => {
            if (!role) return false;
            if (typeof role === "string") {
              return role.toLowerCase().includes("admin");
            }
            if (typeof role === "object" && role.authority) {
              return String(role.authority).toLowerCase().includes("admin");
            }
            return false;
          });

    const impersonation = userData.impersonation || {};
    const impersonating =
      impersonation.active ??
      impersonation.impersonating ??
      userData.impersonating ??
      Boolean(impersonation.targetUsername);

    const impersonatedUsername =
      impersonation.targetUsername ||
      userData.impersonatedUsername ||
      userData.targetUsername ||
      null;

    const adminUsername =
      impersonation.adminUsername ||
      impersonation.impersonatorUsername ||
      userData.adminUsername ||
      userData.impersonatorUsername ||
      null;

    return {
      resolvedUsername,
      isAdmin,
      impersonating: Boolean(impersonating),
      impersonatedUsername,
      adminUsername,
    };
  }, []);

  // Check session on mount with retry logic (with mobile-specific handling)
  const checkSession = useCallback(
    async (retryCount = 0) => {
      try {
        const userData = await authApi.me();
        const details = parseSessionDetails(userData);

        if (!details.impersonating) {
          setUsername(details.resolvedUsername);
        }
        setIsAuthenticated(true);
        setSessionDetails((prev) => ({
          isAdmin: details.isAdmin,
          impersonating: prev.impersonating || details.impersonating,
          impersonatedUsername:
            prev.impersonating && details.impersonating && details.impersonatedUsername
              ? details.impersonatedUsername
              : prev.impersonatedUsername || details.impersonatedUsername,
          adminUsername: prev.adminUsername || details.adminUsername,
        }));
        if (details.impersonating || !details.isAdmin) {
          setImpersonationTransitioning(false);
        }
        setSessionCheckInProgress(false);
      } catch (error) {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const maxRetries = isMobile ? 2 : 1;
        const retryDelay = isMobile ? 1000 : 500;

        if (retryCount < maxRetries && localStorage.getItem("username")) {
          setTimeout(() => checkSession(retryCount + 1), retryDelay);
          return;
        }

        handleUnauthorized({ suppressRedirect: true });
        setUsername(null);
        setIsAuthenticated(false);
        setSessionDetails({
          isAdmin: false,
          impersonating: false,
          impersonatedUsername: null,
          adminUsername: null,
        });
        setImpersonationTransitioning(false);
        setSessionCheckInProgress(false);
      }
    },
    [parseSessionDetails]
  );

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const refreshSession = useCallback(() => {
    return checkSession();
  }, [checkSession]);

  useEffect(() => {
    if (!isAuthenticated || sessionCheckInProgress) {
      return;
    }

    const isAdminRoute = location.pathname.startsWith("/admin");

    if (sessionDetails.impersonating) {
      if (isAdminRoute) {
        navigate("/tax-insights", { replace: true });
      }
      return;
    }

    if (sessionDetails.isAdmin && !impersonationTransitioning) {
      if (!isAdminRoute) {
        navigate("/admin/impersonate", { replace: true });
      }
    }
  }, [
    isAuthenticated,
    sessionCheckInProgress,
    sessionDetails.impersonating,
    sessionDetails.isAdmin,
    impersonationTransitioning,
    location.pathname,
    navigate,
  ]);

  const handleLogin = (user) => {
    setUsername(user);
    setIsAuthenticated(true);
    setSessionDetails((prev) => ({
      ...prev,
      impersonating: false,
      impersonatedUsername: null,
      adminUsername: null,
    }));
    // Store username in localStorage for backward compatibility (optional)
    if (user) {
      localStorage.setItem("username", user);
    }
    checkSession(0);
    setImpersonationTransitioning(false);
  };

  const handleDemoLogin = async () => {
    try {
      // Detect mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
                       /iPhone|iPad|iPod/.test(navigator.userAgent);

      const userData = await authApi.login("demo", "demo");

      const initialDelay = isSafari ? 1500 : (isMobile ? 500 : 300);
      await new Promise(resolve => setTimeout(resolve, initialDelay));

      let sessionVerified = false;
      const maxRetries = isSafari ? 5 : 3;
      const retryDelay = isSafari ? 1000 : 500;

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          await authApi.me();
          sessionVerified = true;
          localStorage.setItem("username", "demo");
          handleLogin("demo");
          navigate("/tax-insights");
          break;
        } catch {
          if (attempt < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
          }
        }
      }

      if (!sessionVerified) {
        alert("Demo login succeeded but session could not be established. Please try again.");
      }
    } catch (error) {
      alert("Demo login failed. Please try again.");
    }
  };

  const handleSignup = (user) => {
    setUsername(user);
    setIsAuthenticated(true);
    setSessionDetails((prev) => ({
      ...prev,
      impersonating: false,
      impersonatedUsername: null,
      adminUsername: null,
    }));
    if (user) {
      localStorage.setItem("username", user);
    }
    checkSession(0);
    setImpersonationTransitioning(false);
  };

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint
      await authApi.logout();
    } catch (error) {
      // Continue with logout even if backend request fails
    }
    
    // Clear local storage and state
    localStorage.removeItem("username");
    setUsername(null);
    setIsAuthenticated(false);
    setSessionDetails({
      isAdmin: false,
      impersonating: false,
      impersonatedUsername: null,
      adminUsername: null,
    });
    setImpersonationTransitioning(false);
    navigate("/");
  };

  const handleLogoutFromHeader = () => {
    setUsername(null);
    setIsAuthenticated(false);
    setSessionDetails({
      isAdmin: false,
      impersonating: false,
      impersonatedUsername: null,
      adminUsername: null,
    });
    setImpersonationTransitioning(false);
  };

  const handleExitDemo = async () => {
    try {
      // Call backend logout endpoint to clear session
      await authApi.logout();
    } catch (error) {
      // Continue with logout even if backend request fails
    }
    
    // Use centralized handler to clear all session data (localStorage, sessionStorage, cookies)
    handleUnauthorized({ suppressRedirect: true });
    
    // Clear state
    setUsername(null);
    setIsAuthenticated(false);
    setSessionDetails({
      isAdmin: false,
      impersonating: false,
      impersonatedUsername: null,
      adminUsername: null,
    });
    setImpersonationTransitioning(false);
    
    // Navigate to landing page
    navigate("/");
    
    // Reload to ensure all state is cleared
    window.location.reload();
  };

  const handleImpersonationStart = (sessionPayload = {}) => {
    const targetUsername =
      sessionPayload.impersonatedUsername ||
      sessionPayload.targetUsername ||
      sessionPayload.username ||
      sessionPayload.email ||
      null;

    const impersonator =
      sessionPayload.adminUsername ||
      sessionPayload.impersonatorUsername ||
      sessionPayload.initiatedBy ||
      sessionPayload.adminEmail ||
      sessionPayload.admin ||
      sessionPayload.supportUser ||
      username;

    setSessionDetails((prev) => ({
      ...prev,
      impersonating: true,
      impersonatedUsername: targetUsername || prev.impersonatedUsername,
      adminUsername: impersonator || prev.adminUsername || prev.impersonatedUsername,
      isAdmin: false,
    }));
    setImpersonationTransitioning(true);

    if (targetUsername) {
      setUsername(targetUsername);
    }

    navigate("/tax-insights", { replace: true });
    setTimeout(() => {
      refreshSession();
      setImpersonationTransitioning(false);
    }, 1200);
  };

  const handleStopImpersonation = async () => {
    setSessionDetails((prev) => ({
      ...prev,
      impersonating: false,
      impersonatedUsername: null,
      isAdmin: true,
    }));
    setImpersonationTransitioning(false);
    try {
      await stopImpersonation();
      await refreshSession();
      navigate("/admin/impersonate");
    } catch (error) {
    }
  };

  return (
    <div className="AppContainer">
      <Header
        username={username}
        setModalOpen={setModalOpen}
        onLogout={handleLogoutFromHeader}
        sessionDetails={sessionDetails}
        onStopImpersonation={handleStopImpersonation}
        onExitDemo={handleExitDemo}
        onDemoLogin={handleDemoLogin}
      />
      {sessionDetails.impersonating && (
        <ImpersonationBanner
          impersonatedUsername={sessionDetails.impersonatedUsername || username}
          adminUsername={sessionDetails.adminUsername}
          onStop={handleStopImpersonation}
        />
      )}
      <main className="Content">
        <Routes>
          <Route path="/" element={<HomePage onLogin={handleLogin} />} />
          <Route 
            path="/auth" 
            element={
              isAuthenticated ? (
                <AuthRedirect />
              ) : (
                <AuthPage onLogin={handleLogin} />
              )
            } 
          />
          <Route path="/learning" element={<KnowledgeBase />} />
          <Route path="/blog" element={<BlogGrid />} />
          <Route path="/blog/:id" element={<BlogPost />} />
          <Route path="/api/chat" element={<ChatPage username={username}/>} />
          <Route
            path="/tax-insights"
            element={
              sessionCheckInProgress ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                  <div>Checking session...</div>
                </div>
              ) : isAuthenticated ? (
                <Dashboard
                  setModalOpen={setModalOpen}
                  username={username}
                  setIsOnboardingProcessing={setIsOnboardingProcessing}
                  isOnboardingProcessing={isOnboardingProcessing}
                />
              ) : (
                <AuthPage onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/overview"
            element={
              isAuthenticated ? (
                <OverviewPage username={username} setModalOpen={setModalOpen} />
              ) : (
                <AuthPage onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/admin/impersonate"
            element={
              sessionCheckInProgress ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                  <div>Checking session...</div>
                </div>
              ) : !isAuthenticated ? (
                <AuthPage onLogin={handleLogin} />
              ) : sessionDetails.isAdmin ? (
                <AdminImpersonation
                  refreshSession={refreshSession}
                  sessionDetails={sessionDetails}
                  onImpersonationStart={handleImpersonationStart}
                  currentUsername={username}
                />
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                  <div>You do not have access to this page.</div>
                </div>
              )
            }
          />
          <Route
                      path="/debugDashboard"
                      element={
                        isAuthenticated ? (
                          <DebugDashboard setModalOpen={setModalOpen} username={username} />
                        ) : (
                          <AuthPage onLogin={handleLogin} />
                        )
                      }
                    />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route 
            path="/signup" 
            element={
              isAuthenticated ? (
                <AuthRedirect />
              ) : (
                <SignUp onSignup={handleSignup} />
              )
            } 
          />
          <Route path="/verify-email" element={<EmailVerification onVerificationComplete={handleSignup} />} />
          <Route
            path="/account"
            element={
              isAuthenticated ? (
                <Account username={username} />
              ) : (
                <AuthPage onLogin={handleLogin} />
              )
            }
          />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicies />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />

          <Route exact path="/" component={BlogGrid} />
          {/* <Route path="/pricing" element={<Pricing />} /> */}
          <Route path="/pricing" element={<Navigate to="/tax-insights" replace />} />
<Route path="/tax-savings-calculator" element={<TaxSavingsCalculator />}></Route>
          <Route path="/comparison-table" element={<ComparisonTable />}> </Route>
          <Route 
            path="/profile" 
            element={
              isAuthenticated ? (
                <Profile username={username} setModalOpen={setModalOpen}/>
              ) : (
                <AuthPage onLogin={handleLogin} />
              )
            }
          />
          <Route path="/tax-quiz" element={<TaxSavingsQuiz />} />
          <Route 
            path="/liquidation-calculator" 
            element={
              sessionCheckInProgress ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                  <div>Checking session...</div>
                </div>
              ) : isAuthenticated ? (
                <TaxScenarioCalculator username={username} />
              ) : (
                <AuthPage onLogin={handleLogin} />
              )
            }
          />
        </Routes>
      </main>
      <Footer />
      <Modal
        username={username}
        isOpen={isModalOpen}
        onClose={() => setModalOpen({ open: false, type: "account" })}
      />
    </div>
  );
};

export default App;
